/**
 * The root state is used for global application state like navigational state and authentication state
 */
import {
    createSessionTimer,
    doLogout,
    fetchAuthUser,
    getAuthSSOLoginUrl,
    isAuthorized,
    refreshSession,
} from "../../lib/auth";
import router from "../../router";
import _ from "lodash";

const state = {
    navigationWarningEnabled: false,
    navigationDrawer: true,
    authUser: null,
    authError: null,
    loginUrl: null,
    originalRouteName: null,
    sessionExpiresAt: null,
    sessionExpirationTimer: null,
    lastActiveTime: -1,
    showSessionExpiringDialog: false,
    globalSearch: null,
};

const getters = {
    isNavigationWarningEnabled: (state) => state.navigationWarningEnabled,
    isNavigationDrawerOpen: (state) => state.navigationDrawer,
    getGlobalSearch: (state) => state.globalSearch,
    getAuthUser: (state) => state.authUser,
    loginUrl: (state) => state.loginUrl,
    isAuthenticated: (state) => {
        return state.authUser !== null;
    },
    isAuthorized: (state) => {
        return (permissionString) => {
            return isAuthorized(permissionString, state.authUser);
        };
    },
    isAuthorizedForOneOf: (state) => {
        return (...permissionStrings) => {
            const match = permissionStrings.find((permissionString) =>
                isAuthorized(permissionString, state.authUser)
            );
            return !!match;
        };
    },
    getAuthError: (state) => state.authError,
    getEmail: (state) => {
        return _.get(state, "authUser.email");
    },
    sessionExpiresAt: (state) => state.sessionExpiresAt,
    showSessionExpiringDialog: (state) => state.showSessionExpiringDialog,
};

function buildSetAuthUserParams(authUser, dispatch) {
    return {
        authUser,
        sessionRefreshCallback: buildRefreshSessionCallback(dispatch),
        sessionExpiringCallback: () => {
            dispatch("showSessionExpiringDialog");
        },
        sessionExpiredCallback: () => {
            dispatch("logout");
        },
    };
}

function buildRefreshSessionCallback(dispatch) {
    return (authUser) => {
        dispatch("setAuthUser", buildSetAuthUserParams(authUser, dispatch));
    };
}

function handleAuthState(authUser, commit, dispatch) {
    commit("setAuthUser", buildSetAuthUserParams(authUser, dispatch));
}

const actions = {
    openNavigationDrawer({ commit }) {
        commit("openNavigationDrawer");
    },
    closeNavigationDrawer({ commit }) {
        commit("closeNavigationDrawer");
    },
    setGlobalSearch({ commit }, text) {
        commit("setGlobalSearch", text);
    },
    clearGlobalSearch({ commit }) {
        commit("clearGlobalSearch", null);
    },
    setAuthConfig({ commit }, authConfig) {
        commit("setAuthConfig", authConfig);
    },
    setAuthUser({ commit }, authUser) {
        commit("setAuthUser", authUser);
    },
    refreshSession({ commit, dispatch }) {
        return refreshSession()
            .then((authUser) => {
                handleAuthState(authUser, commit, dispatch);
                console.log("Session refreshed: ", authUser);
            })
            .catch((err) => {
                console.log("User refresh session failed: ", err);
                doLogout().finally(() => {
                    commit("setAuthUser", null);
                    router.push({ name: "Home" });
                });
            });
    },
    checkAuthState({ commit, dispatch }) {
        return fetchAuthUser()
            .then((authUser) => {
                handleAuthState(authUser, commit, dispatch);
            })
            .catch(() => {
                console.log("checkAuthState: User not authenticated");
                commit("setAuthUser", null);
            });
    },
    logout({ commit, getters }) {
        if (getters.isAuthenticated) {
            doLogout().finally(() => {
                commit("setAuthUser", null);
                router.push({ name: "Home" });
            });
        }
    },
    setAuthError({ commit }, authError) {
        commit("setAuthError", authError);
    },
    updateLastActiveTime({ commit }, lastActiveTime) {
        commit("setLastActiveTime", lastActiveTime);
    },
    showSessionExpiringDialog({ commit }) {
        commit("showSessionExpiringDialog");
    },
    closeSessionExpiringDialog({ commit }) {
        commit("closeSessionExpiringDialog");
    },
};

const mutations = {
    setNavigationWarning: (state) => {
        state.navigationWarningEnabled = true;
    },
    clearNavigationWarning: (state) => {
        state.navigationWarningEnabled = false;
    },
    clearGlobalSearch(state) {
        state.globalSearch = null;
    },
    openNavigationDrawer: (state) => {
        state.navigationDrawer = true;
    },
    closeNavigationDrawer: (state) => {
        state.navigationDrawer = false;
    },
    setGlobalSearch: (state, text) => {
        state.globalSearch = text;
    },
    setNavigationDrawer({ commit }, value) {
        state.navigationDrawer = value;
    },
    setAuthConfig(state, { cognitoDomain, cognitoClientId }) {
        state.loginUrl = getAuthSSOLoginUrl(cognitoDomain, cognitoClientId);
    },
    setAuthUser(state, params) {
        state.showSessionExpiringDialog = false;
        if (params) {
            const {
                authUser,
                sessionRefreshCallback,
                sessionExpiringCallback,
                sessionExpiredCallback,
            } = params;
            state.authUser = authUser;
            if (authUser) {
                state.sessionExpiresAt = new Date(authUser.sessionExpirationTime);
                state.sessionIdleTimer = createSessionTimer({
                    state,
                    sessionRefreshCallback,
                    sessionExpiringCallback,
                    sessionExpiredCallback,
                });
            }
        } else {
            state.authUser = null;
            state.sessionExpiresAt = null;
            clearInterval(state.sessionIdleTimer);
            state.sessionIdleTimer = null;
        }
    },
    setAuthError(state, authError) {
        state.authError = authError;
    },
    setLastActiveTime(state, lastActiveTime) {
        state.lastActiveTime = lastActiveTime;
    },
    showSessionExpiringDialog(state) {
        state.showSessionExpiringDialog = true;
    },
    closeSessionExpiringDialog(state) {
        state.showSessionExpiringDialog = false;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
